// src/stores/caseStore.ts
import { useCaseChannelSubscription } from '@/composables/useChannelSubscription'
import { useCaseDocumentProcessing } from '@/composables/useDocumentProcessing'
import {
  AiAugmentedByMapperKeysEnum,
  type CustomerCase as Case,
  type CasesPartialUpdateData,
  CasesService,
  type CustomerCaseData,
  type CustomerCaseDataChange,
  type CustomerEntity,
  type CustomerEntityWithRelationship,
  DocumentsService,
  EntitiesService,
  type GenericDataMapper,
  type LawData,
  RelationshipTypeEnum,
  type CustomerAppointment,
  type Document
} from '@/types/generated'
import { WebSocketMessageType } from '@/utils/channels/types'
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { emitter } from './eventEmitter'

// Define the store function
export const useCaseStore = defineStore('caseStore', () => {
  const caseRecord = ref<Case | null>(null)
  const appointments = ref<CustomerAppointment[]>([])
  const appointmentProposals = ref<any[]>([])
  const documents = ref<Document[]>([])
  const isLoadingCase = ref<boolean>(false)
  const isLoadingAppointments = ref<boolean>(false)
  const isLoadingAppointmentProposals = ref<boolean>(false)
  const isLoadingDocuments = ref<boolean>(false)
  const isSavingCase = ref<boolean>(false)
  const caseLoadError = ref<Error | null>(null)

  // Centralized WebSocket subscription for the entire case
  const caseUuid = computed(() => caseRecord.value?.uuid || '')
  const caseWebSocketSubscription = useCaseChannelSubscription(caseUuid, {
    disableAutoLifecycle: true // Manual control - subscribe when case loads, unsubscribe when case changes
  })

  // Use the document processing composable for case documents
  const documentProcessor = useCaseDocumentProcessing(caseRecord)

  // Set up case refresh callback for when documents complete processing
  documentProcessor.setRefreshCallback(async (documentUuid?: string) => {
    if (caseRecord.value?.uuid) {
      const caseUuid = caseRecord.value.uuid

      // Fetch updated documents
      await fetchCaseDocuments(caseUuid)

      // Also fetch full case to update case data metadata (entities, law data, etc.)
      // that may have been updated from document processing
      try {
        const updatedCase = await CasesService.casesRetrieve({ uuid: caseUuid })
        if (updatedCase && caseRecord.value) {
          // Update case record with fresh data including metadata
          // Preserve documents we just fetched
          if ('data' in updatedCase && updatedCase.data) {
            caseRecord.value = {
              ...caseRecord.value,
              data: updatedCase.data,
              documents: caseRecord.value.documents
            } as Case
          }
        }
      } catch (error) {
        console.error('[CaseDetailStore] Error fetching case data after document processing', {
          caseUuid,
          documentUuid,
          error
        })
      }
    }
  })

  // Watch for case document processing messages from case channel
  watch(caseWebSocketSubscription.lastMessage, (message) => {
    if (message && message.type === WebSocketMessageType.CASE_DOCUMENT_PROCESSING) {
      // Handle case document processing messages for case documents
      documentProcessor.handleCaseDocumentProcessingMessage(message)

      // Also update the document in caseRecord.documents directly for immediate UI update
      if (message.data?.document_uuid && caseRecord.value?.documents) {
        const docIndex = caseRecord.value.documents.findIndex(
          (d) => d.uuid === message.data.document_uuid
        )
        if (docIndex !== -1) {
          const updatedDoc = {
            ...caseRecord.value.documents[docIndex],
            metadata_enrichment_is_finished: message.data.status === 'FINISHED',
            metadata_enrichment_error: message.data.status === 'FAILED' ? message.data.error : null
          }

          // Reassign the array to trigger reactivity
          caseRecord.value = {
            ...caseRecord.value,
            documents: [
              ...caseRecord.value.documents.slice(0, docIndex),
              updatedDoc,
              ...caseRecord.value.documents.slice(docIndex + 1)
            ]
          }
        }
      }
    }
  })

  // Actions (asynchronous actions such as fetching data)
  async function fetchCase(uuid: string) {
    caseRecord.value = (await CasesService.casesRetrieve({ uuid: uuid })) as Case
  }

  /**
   * Fetch only documents for a case and update the case record.
   *
   * :param uuid: The UUID of the case to fetch documents for
   * :return: None
   */
  async function fetchCaseDocuments(uuid: string) {
    if (!caseRecord.value) return

    const response = await DocumentsService.documentsList({
      caseUuid: uuid
    })

    // Update only the documents property
    if (response.results) {
      caseRecord.value = {
        ...caseRecord.value,
        documents: response.results
      }
    }
  }

  async function fetchAppointments(uuid: string) {
    isLoadingAppointments.value = true
    try {
      // The endpoint returns an array directly, not a paginated response
      const response = await CasesService.casesAppointmentsList({ uuid })
      appointments.value = response as any as CustomerAppointment[]
    } catch (error) {
      console.error('Error fetching appointments:', error)
      appointments.value = []
    } finally {
      isLoadingAppointments.value = false
    }
  }

  async function fetchDocuments(uuid: string) {
    isLoadingDocuments.value = true
    try {
      // The endpoint returns an array directly, not a paginated response
      const response = await CasesService.casesDocumentsList({ uuid })
      documents.value = response as any as Document[]
    } catch (error) {
      console.error('Error fetching documents:', error)
      documents.value = []
    } finally {
      isLoadingDocuments.value = false
    }
  }

  async function fetchAppointmentProposals(uuid: string) {
    isLoadingAppointmentProposals.value = true
    try {
      // The endpoint returns an array directly, not a paginated response
      const response = await CasesService.casesAppointmentProposalsRetrieve({ uuid })
      appointmentProposals.value = response as any
    } catch (error) {
      console.error('Error fetching appointment proposals:', error)
      appointmentProposals.value = []
    } finally {
      isLoadingAppointmentProposals.value = false
    }
  }

  // New method with error handling and loading state tracking
  async function fetchCaseWithErrorHandling(uuid: string) {
    isLoadingCase.value = true
    caseLoadError.value = null

    try {
      caseRecord.value = (await CasesService.casesRetrieve({ uuid: uuid })) as Case

      return caseRecord.value
    } catch (error) {
      console.error('Error fetching case:', error)
      caseLoadError.value = error instanceof Error ? error : new Error(String(error))
      throw error
    } finally {
      isLoadingCase.value = false
    }
  }

  const lawSpecificMappers = computed<GenericDataMapper[]>(() => {
    if (!caseRecord.value?.data) return []
    return caseRecord.value?.data.mappers.filter(
      (mapper) =>
        mapper.is_law_specific_field && mapper.law_field !== undefined && mapper.law_field !== null
    ) as GenericDataMapper[]
  })

  const nonEmptyLawSpecificMappers = computed<GenericDataMapper[]>(() => {
    return lawSpecificMappers.value.filter((mapper) => {
      const [parent, field] = mapper.key.split('.') as [keyof LawData, string]
      // Type assertion since we know parent will be 'law' for law specific fields
      const parentData = caseRecord.value?.data?.[parent as 'law']
      if (!parentData || typeof parentData !== 'object') return false

      // Access the nested field
      const value = (parentData as any)[field]

      // Check for empty values
      if (value === undefined || value === null || value === '') return false
      if (Array.isArray(value) && value.length === 0) return false
      if (typeof value === 'object' && Object.keys(value).length === 0) return false

      return true
    })
  })

  /**
   * Ensure WebSocket is subscribed for the current case.
   * Safe to call multiple times - will only subscribe if needed.
   */
  function ensureWebSocketSubscribed() {
    if (caseRecord.value?.uuid && !caseWebSocketSubscription.isSubscribed.value) {
      caseWebSocketSubscription.subscribe()
    }
  }

  /**
   * Unsubscribe from WebSocket.
   * Called when navigating away from all case detail routes.
   */
  function disconnectWebSocket() {
    caseWebSocketSubscription.unsubscribe()
  }

  async function loadCase(uuid: string, options?: { waitForRelatedData?: boolean }) {
    // Cleanup previous WebSocket subscription when switching cases
    if (caseRecord.value?.uuid && caseRecord.value.uuid !== uuid) {
      caseWebSocketSubscription.unsubscribe()
    }

    // Clear document processing states when switching cases
    documentProcessor.clearDocumentProcessingStates()

    // Subscribe to WebSocket for the new case
    // This will be managed by route-based lifecycle, but we ensure it here too
    ensureWebSocketSubscribed()

    // Set loading states to true before starting
    isLoadingAppointments.value = true
    isLoadingAppointmentProposals.value = true
    isLoadingDocuments.value = true

    try {
      await fetchCase(uuid)

      const relatedDataPromise = Promise.all([
        fetchAppointments(uuid),
        fetchAppointmentProposals(uuid),
        fetchDocuments(uuid)
      ])

      if (options?.waitForRelatedData) {
        await relatedDataPromise
      } else {
        relatedDataPromise.catch((error) => {
          console.error('Error loading case related data:', error)
        })
      }
    } catch (error) {
      console.error('Error loading case:', error)
      throw error
    }
  }

  // Helper function to optimistically remove documents from the case
  function removeDocumentsOptimistically(documentUuids: string[]) {
    // Remove documents from the separate documents array
    documents.value = documents.value.filter((doc) => !documentUuids.includes(doc.uuid))
  }

  function updateEntity(entityUuid: string, updates: Record<string, any>) {
    if (!caseRecord.value?.data.entities) return

    const entityIndex = caseRecord.value.data.entities.findIndex(
      (e) => e.entity?.uuid === entityUuid
    )
    if (entityIndex === -1) return

    Object.assign(caseRecord.value.data.entities[entityIndex].entity!, updates)
  }

  async function addEntity(
    entity: CustomerEntity,
    relationshipType: RelationshipTypeEnum = RelationshipTypeEnum.ENTITIES_OTHER
  ) {
    const response = await EntitiesService.entitiesCreate({
      requestBody: entity
    })
    const relationshipResponse = await EntitiesService.entitiesAddCaseDataRelationshipCreate({
      uuid: response.uuid,
      requestBody: {
        case_data_uuid: caseRecord.value?.data.uuid as string,
        relationship_type: relationshipType
      }
    })
    caseRecord.value?.data.entities.push(relationshipResponse as CustomerEntityWithRelationship)
    return relationshipResponse
  }

  async function deleteEntity(entityRelationShip: CustomerEntityWithRelationship) {
    if (!entityRelationShip || !entityRelationShip.entity || !entityRelationShip.entity.uuid) return

    // Check if the entity has relationships
    if (!entityRelationShip.relationships || entityRelationShip.relationships.length === 0) {
      await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
        uuid: entityRelationShip.entity.uuid,
        requestBody: {
          case_data_uuid: caseRecord.value?.data.uuid as string,
          remove_case_data_association_if_no_relationships_exist: true
        }
      })
    } else {
      for (const relationship of entityRelationShip.relationships) {
        await EntitiesService.entitiesRemoveCaseDataRelationshipCreate({
          uuid: entityRelationShip.entity.uuid,
          requestBody: {
            case_data_uuid: caseRecord.value?.data.uuid as string,
            relationship_uuid: relationship.uuid as string,
            remove_case_data_association_if_no_relationships_exist: true
          }
        })
      }
    }

    // Then we load the case again to get fresh data
    await loadCase(caseRecord.value?.uuid as string)
  }

  async function updateOpponentField(field: string, value: any) {
    if (!caseRecord.value || !caseRecord.value.data || !caseRecord.value.data.opponent) return

    // Update local state
    caseRecord.value = {
      ...caseRecord.value,
      data: {
        ...caseRecord.value.data,
        opponent: {
          ...caseRecord.value.data.opponent,
          [field]: value
        }
      }
    }

    // Persist changes to the backend
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          data: {
            opponent: {
              [field]: value
            }
          }
        }
      })
      return true
    } catch (error) {
      console.error(`Failed to update opponent field ${field}:`, error)
      return false
    }
  }

  async function updateClientField(field: string, value: any) {
    if (!caseRecord.value || !caseRecord.value.data || !caseRecord.value.data.client) return

    // Update local state
    caseRecord.value = {
      ...caseRecord.value,
      data: {
        ...caseRecord.value.data,
        client: {
          ...caseRecord.value.data.client,
          [field]: value
        }
      }
    }

    // Persist changes to the backend
    try {
      await CasesService.casesPartialUpdate({
        uuid: caseRecord.value.uuid,
        requestBody: {
          data: {
            client: {
              [field]: value
            }
          }
        }
      })
      return true
    } catch (error) {
      console.error(`Failed to update client field ${field}:`, error)
      return false
    }
  }

  function hasOpponentFieldAISource(field: string): boolean {
    // if (!caseRecord.value?.data?.opponent?.changes) return false

    // const fieldChanges = caseRecord.value.data.opponent.changes.filter(
    //   (change: CustomerCaseDataChange) => {
    //     const mapperKey = `opponent.${field}`
    //     return change.key === mapperKey || change.field_name === field
    //   }
    // )

    // if (fieldChanges.length === 0) return false

    // // Sort by creation date to get the latest change
    // const latestChange = fieldChanges.sort(
    //   (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    // )[0]

    // return latestChange.source === 'AI' // Assuming 'AI' is the source value
    return false
  }

  function updateCaseChanges(changes: CustomerCaseDataChange[]) {
    if (caseRecord.value) {
      caseRecord.value = {
        ...caseRecord.value,
        data: {
          ...caseRecord.value.data,
          changes
        }
      }
    }
  }

  function getDataWithMapperKey(mapperKey: AiAugmentedByMapperKeysEnum, entityUuid?: string) {
    // TODO: implement this for the entities using the entityUuid
    if (!mapperKey) {
      console.warn('Attempted to get data with undefined mapper key')
      return undefined
    }
    const parts = (mapperKey || '').split('.')
    if (parts.length !== 2) {
      console.warn('Invalid mapper key format:', mapperKey)
      return undefined
    }

    const [parent, field] = parts
    // Safely access the data using the parent key first
    const parentData = caseRecord.value?.data?.[parent as keyof CustomerCaseData]

    // Then access the field on the parent data if it exists
    if (parentData && typeof parentData === 'object') {
      return (parentData as any)[field]
    }

    return undefined
  }

  function updateDataWithMapperKey(
    mapperKey: AiAugmentedByMapperKeysEnum,
    value: any,
    entityUuid?: string
  ) {
    // TODO: implement this for the entities using the entityUuid
  }

  async function saveDataWithMapperKey(
    mapperKey: AiAugmentedByMapperKeysEnum,
    value: any,
    entityUuid?: string
  ) {
    isSavingCase.value = true
    try {
      updateDataWithMapperKey(mapperKey, value)
      const parts = mapperKey.split('.')
      if (parts.length !== 2) {
        console.warn('Invalid mapper key format:', mapperKey)
        return
      }

      const [parent, field] = parts

      // Check if caseRecord exists
      if (!caseRecord.value) {
        console.warn('Cannot save data: caseRecord is null')
        return
      }

      // Check if there are any actual changes on that field
      const parentData = caseRecord.value.data?.[parent as keyof CustomerCaseData]
      if (parentData && typeof parentData === 'object' && (parentData as any)[field] === value) {
        return
      }

      // Create the data object with the nested structure
      const dataToUpdate = {
        data: { [parent]: { [field]: value } }
      }

      // Use type assertion to match the expected CasesPartialUpdateData type
      const updateData = {
        uuid: caseRecord.value.uuid as string,
        requestBody: dataToUpdate
      } as unknown as CasesPartialUpdateData

      const result: Case = (await CasesService.casesPartialUpdate(updateData)) as Case

      // Update the case changes through the store
      if (result.data.changes) {
        updateCaseChanges(result.data.changes)
      }

      // Update the local state directly to ensure UI is in sync
      if (caseRecord.value && caseRecord.value.data) {
        // Ensure the parent object exists
        if (!caseRecord.value.data[parent as keyof CustomerCaseData]) {
          ;(caseRecord.value.data as any)[parent] = {}
        }

        // Update the field value
        const parentObj = caseRecord.value.data[parent as keyof CustomerCaseData]
        if (parentObj && typeof parentObj === 'object') {
          ;(parentObj as any)[field] = value
        }

        // Emit an event to notify components that data has been updated
        emitter.emit('data-updated', {
          key: parent,
          mapperKey: mapperKey,
          value: value
        })
      }
    } finally {
      isSavingCase.value = false
    }
  }

  /**
   * Bulk partial update of DataWithMapperKeys
   * @param updates Object where key is the mapperKey (e.g., 'law.field') and value is the new value
   */
  async function bulkUpdateDataWithMapperKeys(updates: Record<string, any>) {
    if (!caseRecord.value) {
      console.warn('Cannot bulk update: caseRecord is null')
      return
    }
    isSavingCase.value = true
    try {
      // Group updates by parent
      const dataToUpdate: Record<string, Record<string, any>> = {}
      Object.entries(updates).forEach(([mapperKey, value]) => {
        const parts = mapperKey.split('.')
        if (parts.length !== 2) {
          console.warn('Invalid mapper key format:', mapperKey)
          return
        }
        const [parent, field] = parts
        if (!dataToUpdate[parent]) dataToUpdate[parent] = {}
        dataToUpdate[parent][field] = value
      })
      const updateData = {
        uuid: caseRecord.value.uuid as string,
        requestBody: { data: dataToUpdate }
      } as unknown as CasesPartialUpdateData
      const result: Case = (await CasesService.casesPartialUpdate(updateData)) as Case
      // Update the case changes through the store
      if (result.data.changes) {
        updateCaseChanges(result.data.changes)
      }
      // Update the local state for each field
      Object.entries(updates).forEach(([mapperKey, value]) => {
        const parts = mapperKey.split('.')
        if (parts.length !== 2) return
        const [parent, field] = parts
        if (!caseRecord.value!.data[parent as keyof CustomerCaseData]) {
          ;(caseRecord.value!.data as any)[parent] = {}
        }
        const parentObj = caseRecord.value!.data[parent as keyof CustomerCaseData]
        if (parentObj && typeof parentObj === 'object') {
          ;(parentObj as any)[field] = value
        }
        emitter.emit('data-updated', {
          key: parent,
          mapperKey,
          value
        })
      })
    } finally {
      isSavingCase.value = false
    }
  }

  // Return state, getters and actions
  return {
    caseRecord,
    appointments,
    appointmentProposals,
    documents,
    isLoadingCase,
    isLoadingAppointments,
    isLoadingAppointmentProposals,
    isLoadingDocuments,
    isSavingCase,
    caseLoadError,
    lawSpecificMappers,
    nonEmptyLawSpecificMappers,
    loadCase,
    fetchAppointments,
    fetchAppointmentProposals,
    fetchDocuments,
    removeDocumentsOptimistically,
    fetchCaseWithErrorHandling,
    updateEntity,
    updateCaseChanges,
    updateOpponentField,
    hasOpponentFieldAISource,
    updateClientField,
    getDataWithMapperKey,
    updateDataWithMapperKey,
    saveDataWithMapperKey,
    addEntity,
    deleteEntity,
    documentProcessor,
    bulkUpdateDataWithMapperKeys,
    // WebSocket lifecycle management methods
    ensureWebSocketSubscribed,
    disconnectWebSocket,
    // Expose centralized WebSocket connection for components (read-only, type-safe)
    caseWebSocket: {
      lastMessage: computed(() => caseWebSocketSubscription.lastMessage.value ?? null),
      isConnected: caseWebSocketSubscription.isConnected,
      connectionStatus: caseWebSocketSubscription.connectionStatus,
      subscribe: caseWebSocketSubscription.subscribe,
      unsubscribe: caseWebSocketSubscription.unsubscribe
    }
  }
})
